import pusherJs from 'pusher-js';

import { Bus } from '../utils/bus';
import { unix } from '../utils/datetime';
import {
  PusherChannel,
  PusherEvent,
  PusherEventCreator,
  PusherEventName,
  PusherEventPayload,
  PusherSubscriptionError,
} from './types';
import Logger from '../utils/logger';

export async function createPusher({
  sessionToken,
  sessionCluster,
  socketKey,
  socketCluster,
}: {
  sessionToken: string | undefined;
  sessionCluster: string | undefined;
  socketKey: string | undefined;
  socketCluster: string | undefined;
}) {
  if (!sessionToken) return null;
  if (!sessionCluster) return null;
  if (!socketKey) throw new Error('socket information missing');
  if (!socketCluster) throw new Error('socket information missing');

  const pusher = new pusherJs(socketKey, {
    cluster: socketCluster,
    authEndpoint: `${sessionCluster}/pusher/auth`,
    auth: {
      headers: {
        Authorization: sessionToken,
      },
    },
  });

  pusher.connection.bind('connected', () => {
    Logger.debug('Pusher connection established');
  });

  pusher.connection.bind('failed', (error: any) => {
    Logger.error(error);
  });

  return pusher;
}

/**
 * Pusher channel map by pusher event name. Used to find out
 * the channel on which the event should be forwarded
 */
export const eventCreatorsByEventName: {
  [key in PusherEventName]?: PusherEventCreator<any, PusherEventName>;
} = {};

export const pusherBus = new Bus<PusherEvent<any, PusherEventName>>();

export const subscribeToPusherChannel = (
  userId: MongoId,
  pusher: pusherJs,
  channelIdentifier: {
    /**
     * unique channel id used for channel subscription
     * one of three private channels are allowed as id:
     *   1. private-course-${courseId} e.g. private-course-670949148b1d251ff6e085ce
     *   2. private-courseTeam-${courseId} e.g. private-courseTeam-670949148b1d251ff6e085ce
     *   3. private-user-${userId} e.g. private-user-5e4e02ea03570666e1b93157
     * */
    id: `private-${string}`;
    /** name used identify channels by name */
    name: PusherChannel;
  }
) => {
  const channel = pusher.subscribe(channelIdentifier.id);

  channel.bind('pusher:subscription_succeeded', () => {
    Logger.debug(`Subscribed to channel: ${channelIdentifier.name}`);
  });

  channel.bind('pusher:subscription_error', (error: PusherSubscriptionError) => {
    if (error.status === 403) {
      unsubscribe();
      // TODO: handle auth error
    }
    Logger.error(`Subscription to channel ${channelIdentifier.name} failed`);
    Logger.error(error);
  });

  channel.bind_global((eventName: PusherEventName, data: PusherEventPayload<{}>) => {
    const createEvent = eventCreatorsByEventName[eventName];

    // check if received event is bound to this channel
    if (!createEvent?.channels.includes(channelIdentifier.name)) return;

    const event = createEvent({ ...data, timestamp: unix() }, channelIdentifier.name);

    /**
     * Ignore pusher for same user.
     *
     * FIXME: ideally we should ignore the event only for the user
     * on the same device or browser tab. We can fix this if the server
     * sends a token that is unique for every pusher connection.
     */
    const isSameUser = event.payload.sender && event.payload.sender.userId === userId;

    if (isSameUser && event.meta.ignoreForSameUser) return;

    Logger.debug('Pusher event received:', event);

    pusherBus.broadcast(event);
  });

  const unsubscribe = () => {
    Logger.debug(`Unsubscribing from channel: ${channelIdentifier.name}`);
    pusher.unsubscribe(channelIdentifier.id);
  };

  return unsubscribe;
};
